import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('failed');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="checkout-success-page">
      <div className="w-full max-w-md text-center">
        {status === 'loading' && (
          <div className="animate-fade-up">
            <Loader2 className="w-16 h-16 text-[#0055FF] mx-auto mb-6 animate-spin" />
            <h1 className="font-['Outfit'] text-2xl font-medium mb-4">Verifica pagamento in corso...</h1>
            <p className="text-[#A1A1AA]">Stiamo confermando il tuo pagamento. Un momento...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-fade-up">
            <div className="w-20 h-20 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
            </div>
            <h1 className="font-['Outfit'] text-3xl font-medium mb-4">Pagamento completato!</h1>
            <p className="text-[#A1A1AA] mb-8">
              Grazie per il tuo acquisto. La tua licenza è stata attivata e riceverai 
              un'email con i dettagli a breve.
            </p>
            
            {paymentDetails && (
              <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 mb-8 text-left">
                <h3 className="font-['Outfit'] font-medium mb-4">Dettagli ordine</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">Totale pagato:</span>
                    <span>€{(paymentDetails.amount_total / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">ID Sessione:</span>
                    <span className="font-mono text-xs">{sessionId?.slice(0, 20)}...</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard">
                <Button className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full px-8" data-testid="go-to-dashboard-btn">
                  Vai alla Dashboard
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="border-white/20 rounded-full px-8" data-testid="go-home-btn">
                  Torna alla Home
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="animate-fade-up">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="font-['Outfit'] text-3xl font-medium mb-4">Pagamento non riuscito</h1>
            <p className="text-[#A1A1AA] mb-8">
              Si è verificato un problema durante il pagamento. Nessun addebito è stato effettuato.
              Riprova o contatta il supporto.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing">
                <Button className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full px-8" data-testid="retry-btn">
                  Riprova
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white/20 rounded-full px-8" data-testid="contact-support-btn">
                  Contatta Supporto
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
